// Типизированные функции для каждого эндпоинта контракта docs/api-contract.md.
import { request } from './client';
import type {
  AdminOrg,
  AdminUser,
  AdminUserCard,
  AdminUsersPage,
  AuditEntry,
  AuditFilter,
  CaseDetail,
  CaseFile,
  CaseListItem,
  CaseStatus,
  ClinicPartnerRow,
  CreateCaseRequest,
  DoctorTab,
  FileKind,
  LabPartnerRow,
  LabTab,
  Me,
  Message,
  OrgType,
  Page,
  PatchCaseRequest,
  PatientRow,
  Ref,
  Side,
  SignedUrl,
  SupportSession,
  TransitionRequest,
  UploadTicket,
  UserInput,
  UserRoleFilter,
  UserStatusFilter,
  UUID,
} from './types';

/** Список, где next_cursor может отсутствовать (пациенты, сообщения, участники). */
export interface Items<T> {
  items: T[];
  next_cursor?: string | null;
}

export const LIST_LIMIT = 200;

/** Забирает все страницы списка с курсорной пагинацией. */
export async function fetchAllPages<T>(
  fetchPage: (cursor: string | undefined) => Promise<Items<T>>,
): Promise<T[]> {
  const all: T[] = [];
  let cursor: string | undefined;
  for (let guard = 0; guard < 500; guard++) {
    const page = await fetchPage(cursor);
    all.push(...page.items);
    if (!page.next_cursor) break;
    cursor = page.next_cursor;
  }
  return all;
}

const enc = encodeURIComponent;

/* ---------- Профиль ---------- */
export const getMe = (signal?: AbortSignal) => request<Me>('/me', { signal });

/* ---------- Кейсы ---------- */
export type CaseTab = DoctorTab | LabTab;
export type CaseStats = Partial<Record<CaseTab, number>>;

export interface CaseListParams {
  side: Side;
  org?: UUID;
  tab?: CaseTab;
  q?: string;
}

export const listCases = (p: CaseListParams, cursor?: string, signal?: AbortSignal) =>
  request<Page<CaseListItem>>('/cases', {
    query: { side: p.side, org: p.org, tab: p.tab, q: p.q, limit: LIST_LIMIT, cursor },
    signal,
  });

export const listAllCases = (p: CaseListParams, signal?: AbortSignal) =>
  fetchAllPages((cursor) => listCases(p, cursor, signal));

export const getCaseStats = (side: Side, org?: UUID, signal?: AbortSignal) =>
  request<CaseStats>('/cases/stats', { query: { side, org }, signal });

export const createCase = (body: CreateCaseRequest) =>
  request<CaseDetail>('/cases', { method: 'POST', body });

export const getCase = (id: UUID, signal?: AbortSignal) =>
  request<CaseDetail>(`/cases/${enc(id)}`, { signal });

export const transitionCase = (id: UUID, body: TransitionRequest) =>
  request<CaseDetail>(`/cases/${enc(id)}/transitions`, { method: 'POST', body });

export const patchCase = (id: UUID, body: PatchCaseRequest) =>
  request<CaseDetail>(`/cases/${enc(id)}`, { method: 'PATCH', body });

export const requestRescan = (id: UUID, reason: string, version: number) =>
  request<CaseDetail>(`/cases/${enc(id)}/rescan`, { method: 'POST', body: { reason, version } });

export const cancelRescan = (id: UUID, version: number) =>
  request<CaseDetail>(`/cases/${enc(id)}/rescan`, { method: 'DELETE', query: { version } });

/* ---------- Файлы ---------- */
export const createFileTicket = (caseId: UUID, body: { kind: FileKind; name: string; size: number }) =>
  request<UploadTicket>(`/cases/${enc(caseId)}/files`, { method: 'POST', body });

export const completeFile = (caseId: UUID, fileId: UUID) =>
  request<CaseFile>(`/cases/${enc(caseId)}/files/${enc(fileId)}/complete`, { method: 'POST' });

export const getFileUrl = (
  caseId: UUID,
  fileId: UUID,
  variant: 'preview' | 'original',
  signal?: AbortSignal,
) => request<SignedUrl>(`/cases/${enc(caseId)}/files/${enc(fileId)}/url`, { query: { variant }, signal });

export const deleteFile = (caseId: UUID, fileId: UUID) =>
  request<void>(`/cases/${enc(caseId)}/files/${enc(fileId)}`, { method: 'DELETE' });

/* ---------- Переписка ---------- */
export const listMessages = (caseId: UUID, signal?: AbortSignal) =>
  request<Items<Message>>(`/cases/${enc(caseId)}/messages`, { signal });

/** side нужен, когда пользователь состоит и в клинике, и в лаборатории кейса. */
export const postMessage = (caseId: UUID, text: string, side?: 'doctor' | 'lab') =>
  request<Message>(`/cases/${enc(caseId)}/messages`, { method: 'POST', body: { text, side } });

/* ---------- Пациенты и организации ---------- */
export const listPatients = (p: { org?: UUID; q?: string }, cursor?: string, signal?: AbortSignal) =>
  request<Items<PatientRow>>('/patients', {
    query: { org: p.org, q: p.q, limit: LIST_LIMIT, cursor },
    signal,
  });

export const listAllPatients = (p: { org?: UUID; q?: string }, signal?: AbortSignal) =>
  fetchAllPages((cursor) => listPatients(p, cursor, signal));

export const getLabPartners = (labId: UUID, signal?: AbortSignal) =>
  request<Items<LabPartnerRow>>(`/orgs/${enc(labId)}/partners`, { signal });

export const getClinicPartners = (clinicId: UUID, signal?: AbortSignal) =>
  request<Items<ClinicPartnerRow>>(`/orgs/${enc(clinicId)}/partners`, { signal });

export const getMembers = (orgId: UUID, signal?: AbortSignal) =>
  request<Items<Ref>>(`/orgs/${enc(orgId)}/members`, { signal });

/* ---------- Админка ---------- */
export const listUsers = (
  p: { role: UserRoleFilter; status: UserStatusFilter; q?: string },
  cursor?: string,
  signal?: AbortSignal,
) =>
  request<AdminUsersPage>('/admin/users', {
    query: { role: p.role, status: p.status, q: p.q, limit: 50, cursor },
    signal,
  });

export const createUser = (body: UserInput) => request<AdminUser>('/admin/users', { method: 'POST', body });

export const getUser = (id: UUID, signal?: AbortSignal) =>
  request<AdminUserCard>(`/admin/users/${enc(id)}`, { signal });

export const patchUser = (id: UUID, body: Partial<UserInput>) =>
  request<AdminUserCard>(`/admin/users/${enc(id)}`, { method: 'PATCH', body });

export type UserAction = 'invite' | 'reset-password' | 'block' | 'unblock' | 'unlock';
export const userAction = (id: UUID, action: UserAction) =>
  request<AdminUserCard>(`/admin/users/${enc(id)}/${action}`, { method: 'POST' });

export const listOrgs = (signal?: AbortSignal) => request<Items<AdminOrg>>('/admin/orgs', { signal });

export const createOrg = (body: { type: OrgType; name: string }) =>
  request<AdminOrg>('/admin/orgs', { method: 'POST', body });

export const patchOrg = (id: UUID, body: { name?: string; status?: 'active' | 'blocked' }) =>
  request<AdminOrg>(`/admin/orgs/${enc(id)}`, { method: 'PATCH', body });

export const addPartnership = (clinic_id: UUID, lab_id: UUID) =>
  request<void>('/admin/partnerships', { method: 'PUT', body: { clinic_id, lab_id } });

export const removePartnership = (clinic_id: UUID, lab_id: UUID) =>
  request<void>('/admin/partnerships', { method: 'DELETE', query: { clinic_id, lab_id } });

export const listAudit = (filter: AuditFilter, cursor?: string, signal?: AbortSignal) =>
  request<Page<AuditEntry>>('/admin/audit', { query: { filter, limit: 50, cursor }, signal });

export const startSupportSession = (user_id: UUID, reason: string) =>
  request<SupportSession>('/admin/support-sessions', { method: 'POST', body: { user_id, reason } });

export const endSupportSession = (id: UUID) =>
  request<void>(`/admin/support-sessions/${enc(id)}`, { method: 'DELETE' });

export type { CaseStatus };
